/* ============================================================
   Search · Provider

   The public surface is { idle(), query(q) → Promise<Group[]> }
   where Group = { id, label, items: Item[] } and
   Item = { id, kind, title, subtitle?, href, icon?, thumb?,
            external?, titleHtml?, subtitleHtml? }.

   IT WAS A MOCK, AND THE MOCK SHIPPED. This file used to hold a
   hard-coded corpus of five invented article titles ("Getting
   started", "Hub OAuth API", …), each linking to `/docs`, plus a
   client-side scorer and a fake 120ms latency. That is what every
   visitor who pressed ⌘K in production was reading. Ranking now
   happens server-side in `DocSearchEngine`, which is the same
   ranking the in-product docs search uses - two boxes that return
   a different order for the same query are two bugs waiting.

   Quick links stay client-side on purpose: they are static site
   navigation, they must be instant, and asking the server for a
   list that never changes is a round trip for nothing.
   ============================================================ */

const ENDPOINT = '/search';

// The idle state. Site navigation, not search results - so it is
// config, it renders with no network, and it is the same list the
// nav's Resources submenu carries.
// EVERY RESOURCE, NOT FOUR OF THEM (pass 398.54). The Knowledge Base and the
// catalogue were missing, so the idle palette answered "where can I go?" with a
// list that left out the two destinations a reader who is not already inside a
// product most often wants. This is the site's own map; it is short enough to
// print in full, and a partial map is worse than none.
const QUICK_LINKS = [
    { title: 'Documentation',  subtitle: 'Guides, references, and tutorials.', href: '/docs',      icon: 'ti-book-2' },
    { title: 'Knowledge Base', subtitle: 'Answers that span every product.',   href: '/knowledge-base',        icon: 'ti-article' },
    { title: 'Changelogs',     subtitle: 'Release notes for every product.',   href: '/changelog', icon: 'ti-history' },
    { title: 'Roadmap',        subtitle: 'What we are building next.',         href: '/roadmap',   icon: 'ti-flag-3' },
    { title: 'Support',        subtitle: 'Get help and open a ticket.',        href: '/support',   icon: 'ti-lifebuoy' },
    { title: 'jkdevstudio',    subtitle: 'Browse every theme and plugin.',     href: 'https://jkdevstudio.com', icon: 'ti-external-link', external: true },
];

/** Server-rendered seed, when a page wants to enrich the idle state. */
function seed() {
    return (typeof window !== 'undefined' && window.__hubSearchSeed) || {};
}

export const searchProvider = {
    /**
     * Empty-state groups shown when no query is entered.
     * @returns {Array<{id:string, label:string, items:Array}>}
     */
    idle() {
        const s = seed();
        const out = [{
            id: 'quick',
            label: 'Quick actions',
            // NEVER CAPPED. `perGroup` governs how many RESULTS a search group
            // may show; this group is the site map, and a map with rows missing
            // is the one thing it must not be.
            items: (s.quickLinks?.length ? s.quickLinks : QUICK_LINKS).map((it, i) => ({
                id: `idle-quick-${i}`,
                kind: 'link',
                title: it.title,
                href: it.href,
                icon: it.icon || 'ti-arrow-right',
                meta: it.meta || '',
                external: !!it.external,
            })),
        }];

        // Only ever what the SERVER put there. There is no fallback list any
        // more: an invented "recent documentation" section is the exact bug
        // this file was rewritten to remove.
        //
        // THE ROWS ARE PRODUCTS, SO THEY SAY SO (pass 398.2). This group used to
        // be `kind: 'doc'` - a file glyph and a title - for rows that are not
        // articles at all: each one is a PRODUCT whose docs you are being
        // offered. Marked `product`, they render as the shared product row, the
        // same object the archive grids and the footer's catalogue column print,
        // with the product's own colour and mark.
        //
        // The seed carries `perGroup`; slicing to a hard 5 here would have made
        // the setting a lie on the one list it is most visible in.
        const cap = Number(s.perGroup) > 0 ? Number(s.perGroup) : 6;

        if (s.documentation?.length) {
            out.push({
                id: 'recent-docs',
                label: 'Documentation',
                items: s.documentation.slice(0, cap).map((it, i) => ({
                    id: `idle-doc-${i}`,
                    kind: 'product',
                    title: it.title,
                    category: it.category || '',
                    href: it.href,
                    icon: it.icon || 'ti-book',
                    thumb: it.thumb,
                    tile: it.tile,
                    mark: it.mark,
                    meta: it.meta || '',
                    external: !!it.external,
                })),
            });
        }

        // THE KNOWLEDGE BASE, which the idle panel never offered. It is the one
        // section a reader with a problem wants first, and it was reachable only
        // by typing.
        if (s.kb?.length) {
            out.push({
                id: 'recent-kb',
                label: 'Knowledge Base',
                items: s.kb.slice(0, cap).map((it, i) => ({
                    id: `idle-kb-${i}`,
                    kind: 'kb',
                    title: it.title,
                    category: it.category || '',
                    href: it.href,
                    icon: 'ti-article',
                })),
            });
        }

        return out;
    },

    /**
     * Ranked groups from the server.
     *
     * An in-flight request is aborted when the next keystroke arrives, so a slow
     * response for "lic" can never land after and overwrite the results for
     * "license" - the classic out-of-order race in a search-as-you-type box.
     *
     * A failure returns NO GROUPS rather than throwing: the dialog renders that
     * as its empty state, which is the honest answer, and an exception here would
     * leave the loading sliver spinning forever.
     */
    async query(q) {
        this._inflight?.abort();
        const controller = new AbortController();
        this._inflight = controller;

        try {
            const res = await fetch(`${ENDPOINT}?q=${encodeURIComponent(q)}`, {
                headers: { Accept: 'application/json' },
                signal: controller.signal,
            });

            if (!res.ok) return [];

            const groups = await res.json();
            const list = Array.isArray(groups) ? groups : [];

            // ANALYTICS LISTENS, THE PROVIDER DOES NOT CALL IT. The event is
            // dispatched here because this is the only place that knows both the
            // term and how many rows came back; what happens to it is entirely
            // resources/js/frontend/analytics.js's business, and if nobody is
            // listening (no consent, no gtag) the dispatch costs nothing.
            //
            // A SETTLED SEARCH, NOT A KEYSTROKE. Firing per input event would
            // report "ho", "how", "how t" as three searches; this only runs after
            // the caller's debounce, and an aborted request never reaches here.
            window.dispatchEvent(new CustomEvent('hub:search', {
                detail: { term: q, results: list.reduce((n, g) => n + (g.items?.length || 0), 0) },
            }));

            return list;
        } catch (e) {
            // AbortError is the normal path on every keystroke, not a failure.
            return [];
        }
    },
};
